/**
 * "Test": score the chosen sample with the definition as it is on screen —
 * nothing is saved. A dataset item has no answer yet, so one can be pasted in.
 * The result shows the score, pass or fail, the reason, and what each variable read.
 */

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Pill } from "@/components/ui/pill";
import { errorText } from "@/components/ui/states";
import { useProject } from "@/features/projects/hooks";
import { formatMoney, formatScore } from "@/lib/format";
import type { SampleRef } from "./hooks";
import { hasSample, usePreviewEvaluator } from "./hooks";
import type { EvaluatorCreate, PreviewRequest } from "./types";

/** JSON when it parses, the text itself otherwise. */
function asValue(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

const shown = (v: unknown) => {
  const text = v === null || v === undefined ? "—" : typeof v === "string" ? v : JSON.stringify(v, null, 2);
  return text.length > 600 ? `${text.slice(0, 600)}…` : text;
};

export function TestPanel({
  body,
  sampleRef,
  hasOutput,
  problem,
}: {
  body: EvaluatorCreate;
  sampleRef: SampleRef;
  hasOutput: boolean;
  problem: string | null;
}) {
  const [answer, setAnswer] = useState("");
  const preview = usePreviewEvaluator();
  // Testing scores as if saved (and a judge call costs): the server asks for evaluators:write.
  const mayTest = useProject().can("evaluators:write");
  const r = preview.data;
  const chosen = hasSample(sampleRef);
  const pasted = !hasOutput && answer.trim();
  const blocked = problem ?? (!chosen && !pasted ? "Choose a sample or paste an answer." : null);

  const run = () => {
    const req: PreviewRequest = { ...body };
    if (sampleRef.dataset_item_id) req.dataset_item_id = sampleRef.dataset_item_id;
    if (sampleRef.item_result_id) req.item_result_id = sampleRef.item_result_id;
    if (sampleRef.connection_id && sampleRef.trace_id)
      req.trace = {
        connection_id: sampleRef.connection_id,
        trace_id: sampleRef.trace_id,
        ...(sampleRef.observation_id && { observation_id: sampleRef.observation_id }),
      };
    if (pasted) req.sample = { output: asValue(answer) };
    preview.mutate(req);
  };

  if (!mayTest) return null;
  return (
    <section aria-label="Test" className="flex flex-col gap-3 border-t border-line-soft pt-4">
      {!hasOutput && (
        <label className="flex flex-col gap-1.5 text-[13px] font-semibold">
          Answer to test with
          <textarea
            className="inp min-h-16 font-normal"
            placeholder="This sample has no agent answer. Paste one here."
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
          />
        </label>
      )}
      <span className="flex items-center gap-3">
        <Button disabled={!!blocked || preview.isPending} onClick={run}>
          {preview.isPending ? "Testing…" : "Test"}
        </Button>
        {blocked && <span className="text-xs text-muted">{blocked}</span>}
      </span>
      {preview.isError && (
        <p role="alert" className="text-xs text-err">
          {errorText(preview.error, "Could not test the evaluator.")}
        </p>
      )}
      {r && (
        <div
          aria-label="Test result"
          role="region"
          className="flex flex-col gap-2 rounded-lg bg-subtle px-3 py-3"
        >
          <span className="flex items-center gap-2">
            <span className="font-mono text-lg">{formatScore(r.value)}</span>
            <Pill tone={r.passed === false ? "warn" : r.passed ? "run" : "neutral"}>
              {r.passed === false ? "Fail" : r.passed ? "Pass" : r.status}
            </Pill>
            <span className="grow" />
            <span className="src">cost {formatMoney(r.cost_usd)}</span>
          </span>
          {r.reason && <p className="text-xs leading-relaxed text-body">{r.reason}</p>}
          {Object.keys(r.values ?? {}).length > 0 && (
            <section aria-label="What it read" className="flex flex-col gap-1.5 text-xs">
              <span className="font-semibold">What it read</span>
              <dl className="flex flex-col gap-2">
                {Object.entries(r.values).map(([name, value]) => (
                  <div key={name} className="flex flex-col gap-0.5">
                    <dt className="font-mono text-muted">{`{{${name}}}`}</dt>
                    <dd className="rounded-md bg-surface px-2 py-1.5 font-mono text-[11px] break-words whitespace-pre-wrap">
                      {shown(value)}
                    </dd>
                  </div>
                ))}
              </dl>
            </section>
          )}
        </div>
      )}
    </section>
  );
}
